// Canonical free-mail domain list + email/URL domain-normalization helpers.
//
// Previously duplicated three times (workers/shared/freemail.js,
// auger-web/src/composables/useHarmonic.ts, and a hand-written SQL `IN (...)`
// literal in workers/harmonic/core/queue.js) with two independently-evolved
// domain-normalization algorithms. Consolidated here so backend (CommonJS)
// and frontend (Vite/TypeScript) share one list and one set of tested
// behaviors instead of drifting again.

/** Free (consumer webmail) domains — never a company's own domain. */
export const FREE_MAIL_DOMAINS: Set<string> = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
])

export function isFreemail(domain: unknown): boolean {
  return FREE_MAIL_DOMAINS.has(String(domain ?? "").toLowerCase())
}

/**
 * Extract the domain from an email address, or null if the address is
 * malformed or the domain is a freemail provider (i.e. not useful as a
 * company-identifying signal).
 */
export function domainFromEmail(email: unknown): string | null {
  const match = /@([^\s@]+)$/.exec(String(email ?? "").trim())
  if (!match) return null
  const domain = match[1]!.toLowerCase()
  return isFreemail(domain) ? null : domain
}

// ---------------------------------------------------------------------------
// Domain normalization — TWO call-site contracts, not one.
//
// `domainFromUrl` (backend) and `normalizeDomain` (frontend) started as
// independent implementations and, verified against a ~20-case battery
// (bare domains, scheme/www/path/query/port variations, malformed strings,
// empty/non-string input, credentialed URLs), they genuinely diverge:
//
//   input                                => domainFromUrl   | normalizeDomain
//   "not a url with spaces"              => null            | "not a url with spaces"
//   "user@example.com"                   => "example.com"   | "user@example.com"
//   123 (non-string)                     => "0.0.0.123"     | ""
//   "http://user:pass@example.com/path"  => "example.com"   | "user"
//
// That's not incidental drift — it's two different jobs:
//   - domainFromUrl validates whether a raw source-data field IS a real
//     domain (workers/infill/core/extractDomain.js gates on non-null before
//     trusting a value as a company's website domain). It uses `new URL()`
//     in a try/catch and rejects anything unparseable.
//   - normalizeDomain reduces a value for equality comparison
//     (auger-web/src/composables/useHarmonic.ts: "is this Harmonic site the
//     same domain as this contact URL?") and must never throw or force
//     null-handling on callers doing inline `=== ` checks. It uses a
//     forgiving regex chain and always returns a string, best-effort.
//
// Forcing both through one "core" parser would have to pick one philosophy
// (reject-on-doubt vs. always-degrade) and silently change the other
// caller's behavior on inputs it currently handles fine. So: two
// implementations, each preserving its existing algorithm and contract
// byte-for-byte, both exported from this single source of truth for the
// free-mail list they share. See tests/domains.test.ts for the full
// equivalence battery.
// ---------------------------------------------------------------------------

/**
 * Backend contract: strict, validating. Returns the bare lowercased host
 * (www.-stripped) for a real URL or bare domain, or null if `url` is empty
 * or unparseable.
 */
export function domainFromUrl(url: unknown): string | null {
  const raw = String(url ?? "").trim()
  if (!raw) return null
  let host: string
  try {
    host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname
  } catch {
    return null
  }
  return host.replace(/^www\./i, "").toLowerCase() || null
}

/**
 * Frontend contract: best-effort, never throws, never null. Reduces a URL
 * or bare domain to a bare, lowercased host (no scheme/www/path/query/port)
 * for equality comparison. Non-string or empty input yields "".
 */
export function normalizeDomain(urlOrDomain: unknown): string {
  if (!urlOrDomain || typeof urlOrDomain !== "string") return ""
  let s = urlOrDomain.trim().toLowerCase()
  s = s.replace(/^[a-z]+:\/\//, "") // scheme
  s = s.replace(/^www\./, "")
  s = s.split("/")[0] ?? "" // path
  s = s.split("?")[0] ?? "" // query string
  s = s.split(":")[0] ?? "" // port
  return s
}
